import { useState, type ReactNode } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    IconButton,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { DragDropContext, Draggable, Droppable, type DraggableProvidedDragHandleProps, type DropResult } from "@hello-pangea/dnd";
import {
    Add as AddIcon,
    Check as DoneIcon,
    CreateNewFolderOutlined as AddGroupIcon,
    DeleteOutline as DeleteIcon,
    DragIndicator,
    DriveFileMoveOutlined as MoveIcon,
    EditOutlined as RenameIcon,
    ExpandLess,
    ExpandMore,
    UnfoldLess as CollapseAllIcon,
    UnfoldMore as ExpandAllIcon,
    WarningAmber as ReviewIcon,
} from "@mui/icons-material";
import type { Arrangement, ArchivedProvision, PlaybookGroup, Provision, Severity } from "../../services/lensService";
import { SEVERITIES, formatWhen, isValidKey, plural, severityColour, toKey } from "./lensUtils";
import { UNGROUPED, buildSections, moveTo, sectionId, sortGroups, toArrangement, type Section } from "./provisionGroups";
import ConfirmDialog from "./ConfirmDialog";
import GroupNameDialog from "./GroupNameDialog";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4) },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(2) },
    headerActions: { display: "flex", alignItems: "center", gap: theme.spacing(0.5) },
    addForm: { display: "flex", gap: theme.spacing(2), alignItems: "flex-start", marginBottom: theme.spacing(3) },
    pills: { display: "flex", gap: theme.spacing(1.5), flexWrap: "wrap", marginBottom: theme.spacing(2) },
    selectionBar: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        padding: theme.spacing(0.5, 1),
        borderRadius: 4,
        backgroundColor: "#f3f0fe",
    },
    scroll: { maxHeight: 520, overflowY: "auto", marginTop: theme.spacing(1) },
    section: { marginTop: theme.spacing(1) },
    sectionHeader: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.5, 0),
        borderBottom: "1px solid #eee",
    },
    sectionTitle: { fontWeight: 600, flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sectionDragging: { backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" },
    dropZone: { minHeight: 8, borderRadius: 4, transition: "background-color 0.15s" },
    dropZoneOver: { backgroundColor: "#f3f0fe" },
    dropEmpty: { padding: theme.spacing(1, 2), color: "#999" },
    row: { display: "flex", alignItems: "center", backgroundColor: "#fff" },
    rowDragging: { boxShadow: "0 2px 8px rgba(0,0,0,0.15)" },
    handle: { display: "flex", alignItems: "center", color: "#aaa", cursor: "grab" },
    dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginRight: theme.spacing(2) },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    archived: { marginTop: theme.spacing(4), borderTop: "1px solid #eee", paddingTop: theme.spacing(2) },
    archivedRow: { display: "flex", alignItems: "center", gap: theme.spacing(2), padding: theme.spacing(1, 0) },
}));

interface ProvisionListProps {
    provisions: Provision[];
    groups: PlaybookGroup[];
    archived: ArchivedProvision[];
    selectedKey: string | null;
    readOnly: boolean;
    isBusy: boolean;
    // A layout save is in flight; drags wait so each builds on the last.
    isArranging: boolean;
    onSelect: (key: string) => void;
    onCreate: (key: string, name: string, groupId: number | null) => void;
    onRestore: (key: string) => void;
    onArrange: (arrangement: Arrangement, successMessage?: string) => void;
    onCreateGroup: (name: string) => void;
    onRenameGroup: (id: number, name: string) => void;
    onDeleteGroup: (id: number) => void;
}

const toggle = <T,>(set: Set<T>, value: T) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
};

const ProvisionList = ({
    provisions,
    groups,
    archived,
    selectedKey,
    readOnly,
    isBusy,
    isArranging,
    onSelect,
    onCreate,
    onRestore,
    onArrange,
    onCreateGroup,
    onRenameGroup,
    onDeleteGroup,
}: ProvisionListProps) => {
    const { classes, cx, theme } = useStyles();
    const [query, setQuery] = useState("");
    const [severityFilter, setSeverityFilter] = useState<Severity | null>(null);
    const [needsReview, setNeedsReview] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newName, setNewName] = useState("");
    // 0 is Ungrouped.
    const [newGroupId, setNewGroupId] = useState(0);
    const [showArchived, setShowArchived] = useState(false);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [checked, setChecked] = useState<Set<string>>(new Set());
    const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
    const [addingGroup, setAddingGroup] = useState(false);
    const [renaming, setRenaming] = useState<PlaybookGroup | null>(null);
    const [deleting, setDeleting] = useState<PlaybookGroup | null>(null);
    // Grouping controls only show while editing.
    const [editing, setEditing] = useState(false);

    const newKey = toKey(newName);
    const liveClash = newKey ? provisions.find((p) => p.key === newKey) : undefined;
    const archivedClash = newKey ? archived.find((p) => p.key === newKey) : undefined;
    const clashMessage = liveClash
        ? `This playbook already has "${liveClash.name}". Select it in the list to edit it.`
        : archivedClash
          ? `"${archivedClash.name}" was archived ${formatWhen(archivedClash.archivedOn)}. Restore it instead.`
          : undefined;

    const needle = query.trim().toLowerCase();
    const filtering = !!needle || !!severityFilter || needsReview;
    const matches = (p: Provision) =>
        (!severityFilter || p.position.absenceSeverity === severityFilter) &&
        (!needsReview || !!p.reviewNote) &&
        (!needle || p.name.toLowerCase().includes(needle) || p.key.includes(needle));
    const draftCount = provisions.filter((p) => p.position.status === "draft").length;
    const reviewCount = provisions.filter((p) => p.reviewNote).length;

    // Order and grouping can only change on the unfiltered list.
    const editMode = !readOnly && editing;
    const canArrange = !readOnly && !isBusy && !isArranging;
    const canDrag = editMode && canArrange && !filtering;

    const sections = buildSections({ groups, provisions });
    const hasGroups = groups.length > 0;
    const shown = sections
        .map((section) => ({ section, rows: filtering ? section.provisions.filter(matches) : section.provisions }))
        .filter(({ section, rows }) => (filtering ? rows.length > 0 : section.id !== null || hasGroups || rows.length > 0));
    const shownGroups = shown.filter((s) => s.section.id !== null);
    const shownUngrouped = shown.find((s) => s.section.id === null);
    const matchCount = shown.reduce((n, s) => n + s.rows.length, 0);

    // A refresh may have archived a ticked provision.
    const liveKeys = new Set(provisions.map((p) => p.key));
    const ticked = new Set([...checked].filter((key) => liveKeys.has(key)));

    const deletingCount = deleting ? provisions.filter((p) => p.groupId === deleting.id).length : 0;

    const isCollapsed = (section: Section) => !filtering && collapsed.has(sectionId(section.id));

    const setAllCollapsed = (closed: boolean) =>
        setCollapsed(closed ? new Set(sections.map((s) => sectionId(s.id))) : new Set());

    const toggleEditing = () => {
        setEditing((e) => !e);
        setChecked(new Set());
        setMoveAnchor(null);
    };

    const groupName = (id: number | null) => sections.find((s) => s.id === id)?.name ?? UNGROUPED;

    const moveTicked = (targetId: number | null) => {
        setMoveAnchor(null);
        if (ticked.size === 0) return;
        onArrange(
            toArrangement(moveTo(sections, ticked, targetId)),
            `Moved ${plural(ticked.size, "provision")} to ${groupName(targetId)}`,
        );
        setChecked(new Set());
    };

    const onDragEnd = ({ source, destination, type }: DropResult) => {
        if (!destination || !canDrag) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        if (type === "GROUP") {
            const grouped = sections.filter((s) => s.id !== null);
            const [moved] = grouped.splice(source.index, 1);
            grouped.splice(destination.index, 0, moved);
            onArrange(toArrangement([...grouped, ...sections.filter((s) => s.id === null)]));
            return;
        }

        const next = sections.map((s) => ({ ...s, provisions: [...s.provisions] }));
        const from = next.find((s) => sectionId(s.id) === source.droppableId);
        const to = next.find((s) => sectionId(s.id) === destination.droppableId);
        if (!from || !to) return;

        const [moved] = from.provisions.splice(source.index, 1);
        // A collapsed group shows no rows, so a drop onto it goes to the end.
        to.provisions.splice(isCollapsed(to) ? to.provisions.length : destination.index, 0, moved);
        onArrange(toArrangement(next));
    };

    const renderRow = (provision: Provision, index: number) => (
        <Draggable key={provision.key} draggableId={`provision:${provision.key}`} index={index} isDragDisabled={!canDrag}>
            {(dragProvided, dragSnapshot) => (
                <Box
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    className={cx(classes.row, dragSnapshot.isDragging && classes.rowDragging)}
                >
                    {canDrag ? (
                        <Box {...dragProvided.dragHandleProps} className={classes.handle} aria-label="Drag to reorder">
                            <DragIndicator fontSize="small" />
                        </Box>
                    ) : (
                        // The handle props carry the drag's a11y wiring, so they must stay mounted.
                        <Box {...dragProvided.dragHandleProps} style={{ display: "none" }} />
                    )}
                    {editMode && (
                        <Checkbox
                            size="small"
                            checked={ticked.has(provision.key)}
                            onChange={() => setChecked((c) => toggle(c, provision.key))}
                            inputProps={{ "aria-label": `Select ${provision.name || provision.key}` }}
                            style={{ padding: theme.spacing(0.5) }}
                        />
                    )}
                    <ListItemButton
                        dense
                        selected={provision.key === selectedKey}
                        onClick={() => onSelect(provision.key)}
                        style={{ paddingLeft: theme.spacing(1) }}
                    >
                        <span
                            className={classes.dot}
                            style={{ backgroundColor: theme.palette[severityColour[provision.position.absenceSeverity]].main }}
                        />
                        <ListItemText primary={provision.name || provision.key} />
                        {provision.reviewNote && (
                            <ReviewIcon
                                fontSize="small"
                                color="warning"
                                titleAccess="Needs review"
                                style={{ marginRight: theme.spacing(1) }}
                            />
                        )}
                        <Typography
                            variant="caption"
                            style={{
                                fontWeight: 600,
                                color: provision.position.status === "draft" ? "#7a5cf0" : theme.palette.success.main,
                            }}
                        >
                            {provision.position.status === "draft" ? "draft" : "approved"}
                        </Typography>
                    </ListItemButton>
                </Box>
            )}
        </Draggable>
    );

    const renderSection = (
        { section, rows }: { section: Section; rows: Provision[] },
        dragHandle?: DraggableProvidedDragHandleProps | null,
    ): ReactNode => {
        const id = sectionId(section.id);
        const closed = isCollapsed(section);
        const group = section.id === null ? null : (groups.find((g) => g.id === section.id) ?? null);
        const count = filtering ? `${rows.length} of ${section.provisions.length}` : `${section.provisions.length}`;

        return (
            <>
                {(hasGroups || section.id !== null) && (
                    <Box className={classes.sectionHeader}>
                        {group && canDrag ? (
                            <Box {...dragHandle} className={classes.handle} aria-label="Drag to reorder groups">
                                <DragIndicator fontSize="small" />
                            </Box>
                        ) : (
                            group && <Box {...dragHandle} style={{ display: "none" }} />
                        )}
                        <IconButton
                            size="small"
                            onClick={() => setCollapsed((c) => toggle(c, id))}
                            disabled={filtering}
                            aria-label={closed ? `Expand ${section.name}` : `Collapse ${section.name}`}
                        >
                            {closed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                        </IconButton>
                        <Typography variant="body2" className={classes.sectionTitle} title={section.name}>
                            {section.name}
                        </Typography>
                        <Chip size="small" variant="outlined" label={count} />
                        {group && editMode && (
                            <>
                                <Tooltip title="Rename group">
                                    <span>
                                        <IconButton size="small" disabled={!canArrange} onClick={() => setRenaming(group)}>
                                            <RenameIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete group">
                                    <span>
                                        <IconButton size="small" disabled={!canArrange} onClick={() => setDeleting(group)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                )}
                <Droppable droppableId={id} type="PROVISION" isDropDisabled={!canDrag}>
                    {(provided, snapshot) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={cx(classes.dropZone, snapshot.isDraggingOver && classes.dropZoneOver)}
                        >
                            {!closed && rows.map(renderRow)}
                            {!closed && rows.length === 0 && !snapshot.isDraggingOver && hasGroups && (
                                <Typography variant="caption" component="div" className={classes.dropEmpty}>
                                    {canDrag ? "No provisions. Drag one here." : "No provisions."}
                                </Typography>
                            )}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </>
        );
    };

    return (
        <Paper className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Provisions</Typography>
                <Box className={classes.headerActions}>
                    {hasGroups && (
                        <>
                            <Tooltip title="Expand all">
                                <span>
                                    <IconButton size="small" disabled={filtering} onClick={() => setAllCollapsed(false)} aria-label="Expand all">
                                        <ExpandAllIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Collapse all">
                                <span>
                                    <IconButton size="small" disabled={filtering} onClick={() => setAllCollapsed(true)} aria-label="Collapse all">
                                        <CollapseAllIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {!readOnly && (
                        <>
                            <Button
                                size="small"
                                variant={editing ? "contained" : "text"}
                                startIcon={editing ? <DoneIcon /> : <RenameIcon />}
                                onClick={toggleEditing}
                            >
                                {editing ? "Done" : "Edit groups"}
                            </Button>
                            {editing && (
                                <Button size="small" startIcon={<AddGroupIcon />} disabled={!canArrange} onClick={() => setAddingGroup(true)}>
                                    Add group
                                </Button>
                            )}
                            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen((o) => !o)}>
                                Add
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {addOpen && !readOnly && (
                <Box className={classes.addForm}>
                    <TextField
                        size="small"
                        label="Provision name"
                        placeholder="e.g. Holiday entitlement"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        error={!!liveClash}
                        helperText={clashMessage ?? (newKey && `Key: ${newKey}`)}
                        fullWidth
                    />
                    {hasGroups && !archivedClash && (
                        <TextField
                            select
                            size="small"
                            label="Group"
                            value={newGroupId}
                            onChange={(e) => setNewGroupId(Number(e.target.value))}
                            style={{ minWidth: 160 }}
                        >
                            {[...groups].sort((a, b) => a.name.localeCompare(b.name)).map((g) => (
                                <MenuItem key={g.id} value={g.id}>
                                    {g.name}
                                </MenuItem>
                            ))}
                            <MenuItem value={0}>{UNGROUPED}</MenuItem>
                        </TextField>
                    )}
                    {archivedClash ? (
                        <Button
                            variant="contained"
                            disabled={isBusy}
                            onClick={() => {
                                onRestore(archivedClash.key);
                                setNewName("");
                                setAddOpen(false);
                            }}
                        >
                            Restore
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={isBusy || !isValidKey(newKey) || !!liveClash}
                            onClick={() => {
                                onCreate(newKey, newName.trim(), groups.some((g) => g.id === newGroupId) ? newGroupId : null);
                                setNewName("");
                                setAddOpen(false);
                            }}
                        >
                            Add
                        </Button>
                    )}
                </Box>
            )}

            <Box className={classes.pills}>
                {SEVERITIES.map((severity) => {
                    const on = severityFilter === severity;
                    return (
                        <Chip
                            key={severity}
                            size="small"
                            color={severityColour[severity]}
                            variant={on ? "filled" : "outlined"}
                            label={`${provisions.filter((p) => p.position.absenceSeverity === severity).length} ${severity}`}
                            onClick={() => setSeverityFilter(on ? null : severity)}
                        />
                    );
                })}
                {draftCount > 0 && <Chip size="small" variant="outlined" label={`${draftCount} draft`} />}
                {(reviewCount > 0 || needsReview) && (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<ReviewIcon />}
                        variant={needsReview ? "filled" : "outlined"}
                        label={`Needs review (${reviewCount})`}
                        onClick={() => setNeedsReview((on) => !on)}
                    />
                )}
            </Box>

            <TextField
                size="small"
                placeholder="Filter provisions…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                helperText={filtering && editMode ? "Clear the search and filters to drag and reorder." : undefined}
                fullWidth
            />

            {editMode && ticked.size > 0 && (
                <Box className={classes.selectionBar}>
                    <Typography variant="body2" style={{ flexGrow: 1 }}>
                        {ticked.size} selected
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<MoveIcon />}
                        disabled={!canArrange}
                        onClick={(e) => setMoveAnchor(e.currentTarget)}
                    >
                        Move to group
                    </Button>
                    <Button size="small" onClick={() => setChecked(new Set())}>
                        Clear
                    </Button>
                    <Menu anchorEl={moveAnchor} open={!!moveAnchor} onClose={() => setMoveAnchor(null)}>
                        {!hasGroups && <MenuItem disabled>Add a group first</MenuItem>}
                        {sortGroups(groups).map((g) => (
                            <MenuItem key={g.id} onClick={() => moveTicked(g.id)}>
                                {g.name}
                            </MenuItem>
                        ))}
                        {hasGroups && <MenuItem onClick={() => moveTicked(null)}>{UNGROUPED}</MenuItem>}
                    </Menu>
                </Box>
            )}

            <Box className={classes.scroll}>
                {provisions.length === 0 && !hasGroups ? (
                    <Typography variant="body2" className={classes.empty}>
                        No provisions yet. Add one, or draft a set from a precedent.
                    </Typography>
                ) : filtering && matchCount === 0 ? (
                    <Typography variant="body2" className={classes.empty}>
                        No provisions match.
                    </Typography>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="groups" type="GROUP" isDropDisabled={!canDrag}>
                            {(provided) => (
                                <Box ref={provided.innerRef} {...provided.droppableProps}>
                                    {shownGroups.map((entry, index) => (
                                        <Draggable
                                            key={sectionId(entry.section.id)}
                                            draggableId={`group:${entry.section.id}`}
                                            index={index}
                                            isDragDisabled={!canDrag}
                                        >
                                            {(dragProvided, dragSnapshot) => (
                                                <Box
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    className={cx(classes.section, dragSnapshot.isDragging && classes.sectionDragging)}
                                                >
                                                    {renderSection(entry, dragProvided.dragHandleProps)}
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                        {shownUngrouped && <Box className={classes.section}>{renderSection(shownUngrouped)}</Box>}
                    </DragDropContext>
                )}
            </Box>

            {archived.length > 0 && (
                <Box className={classes.archived}>
                    <Button size="small" onClick={() => setShowArchived((s) => !s)}>
                        {showArchived ? "Hide" : "Show"} archived ({archived.length})
                    </Button>
                    {showArchived &&
                        archived.map((provision) => (
                            <Box key={provision.key} className={classes.archivedRow}>
                                <Typography variant="body2" style={{ flexGrow: 1, color: "#777" }}>
                                    {provision.name} · archived {formatWhen(provision.archivedOn)}
                                </Typography>
                                {!readOnly && (
                                    <Button size="small" disabled={isBusy} onClick={() => onRestore(provision.key)}>
                                        Restore
                                    </Button>
                                )}
                            </Box>
                        ))}
                </Box>
            )}

            <GroupNameDialog
                open={addingGroup}
                title="Add a group"
                confirmLabel="Add group"
                groups={groups}
                onClose={() => setAddingGroup(false)}
                onConfirm={onCreateGroup}
            />
            <GroupNameDialog
                open={!!renaming}
                title="Rename group"
                confirmLabel="Rename"
                initialName={renaming?.name ?? ""}
                groups={groups}
                except={renaming?.id}
                onClose={() => setRenaming(null)}
                onConfirm={(name) => renaming && onRenameGroup(renaming.id, name)}
            />
            <ConfirmDialog
                open={!!deleting}
                title={`Delete the group "${deleting?.name ?? ""}"?`}
                confirmLabel="Delete group"
                destructive
                onClose={() => setDeleting(null)}
                onConfirm={() => deleting && onDeleteGroup(deleting.id)}
            >
                {deletingCount > 0
                    ? `Its ${plural(deletingCount, "provision")} move to ${UNGROUPED}. No provision is deleted.`
                    : "The group is empty, so no provision is affected."}
            </ConfirmDialog>
        </Paper>
    );
};

export default ProvisionList;
